import React, { FunctionComponent, useState } from 'react'

import {
  useAmountDueToday,
  useAppRouter,
  useCheckoutState,
  useCrossBorderTrade,
  useIsAfterpayFinanceFeeEligible,
  useIsAfterpayAppGiftCardFlow,
  useIsDeferredShipping,
  useIsExpress,
  useIsShippingOptionNotSelected,
  useIsShippingOptionRequired,
} from '../../state'
import { Terms, TermsWithCheckbox } from '../Terms'
import { Button, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { NotificationError } from '../../model'
import { isNullOrUndefined } from '@afterpay/utils'
import { useTranslation } from '../../utils/convergence'
import { useMoney } from '../Money/useMoney'
import { DockedFooterPortal } from '../DockedFooterPortal'
import { useIsMissingPaymentMethod } from './hooks'
import { Route } from '../../config/router'

export interface SummaryCtaFooterProps {
  clicked?: boolean
  notification?: NotificationError
  onConfirm: () => void
  isSummaryButtonDisabled: boolean
}

export const SummaryCtaFooter: FunctionComponent<SummaryCtaFooterProps> = ({ clicked, onConfirm, isSummaryButtonDisabled }) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { locale } = router
  const isDeferredShipping = useIsDeferredShipping()
  const isExpress = useIsExpress()
  const { initial } = useCheckoutState()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isShippingOptionRequired = useIsShippingOptionRequired()
  const { isCrossBorder } = useCrossBorderTrade()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const [isTermsCheckboxChecked, setTermsCheckboxChecked] = useState<boolean>(false)
  const [showCheckboxErrorMessage, setShowCheckboxErrorMessage] = useState<boolean>(false)
  const isMissingPaymentMethod = useIsMissingPaymentMethod()

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const isChecked = e.target.checked
    setTermsCheckboxChecked(isChecked)
    if (isChecked) {
      setShowCheckboxErrorMessage(false)
    }
  }

  const handleClick = (): void => {
    if (summaryButtonDisabled && isMissingPaymentMethod) {
      // onConfirm first: the page's disabled branch logs PRESSED_SUMMARY_PAGE_CONFIRM_BUTTON_DISABLED
      // and must keep firing unchanged (CASHDAP-3471 keeps the existing metric comparable).
      onConfirm()
      router.push(Route.NEW_PAYMENT_METHOD)
      return
    }
    if (!summaryButtonDisabled && isAFF && !isTermsCheckboxChecked) {
      setShowCheckboxErrorMessage(true)
      return
    }
    onConfirm()
  }

  const amountDueToday = useAmountDueToday()
  const { formatMoney } = useMoney({
    locale,
    currency: amountDueToday?.currency,
    decimalPlaces: 2,
  })

  const summaryButtonDisabled = isSummaryButtonDisabled || isNullOrUndefined(amountDueToday)
  const isShippingOptionNotSelected = useIsShippingOptionNotSelected()

  const getCTAOptions = (): string => {
    const isDeferredShippingCTA = isDeferredShipping || (isExpress && isShippingOptionRequired && !initial.buyNow)
    const isBuyNowCTA = isExpress && initial.buyNow
    const isArcadeSelectShippingCTA = summaryButtonDisabled && isExpress && isShippingOptionNotSelected && isArcadeDesignLanguage
    const isArcadeXBTCTA = isCrossBorder && isArcadeDesignLanguage

    const ctaConditions = [
      { condition: isMissingPaymentMethod, value: 'addPaymentMethod' },
      { condition: isAfterpayAppGiftCardFlow, value: 'checkoutAPGiftCardFlow' },
      { condition: isAFF, value: 'aff' },
      { condition: isArcadeSelectShippingCTA, value: 'selectShipping' },
      { condition: isBuyNowCTA, value: 'buyNow' },
      { condition: isDeferredShippingCTA, value: 'deferredShipping' },
      { condition: isArcadeXBTCTA, value: 'xbtPay' },
    ]

    return ctaConditions.find(({ condition }) => condition)?.value ?? ''
  }

  return (
    <DockedFooterPortal>
      {isAFF ? <TermsWithCheckbox onCheckboxChange={handleCheckboxChange} showErrorMessage={showCheckboxErrorMessage} /> : <Terms />}
      <Button
        size={'Large'}
        padding='Fluid'
        data-dd-action-name='Confirm Checkout'
        loading={clicked}
        onClick={handleClick}
        testNameSpace='summary'
        allowClickOnDisabled
        disabled={summaryButtonDisabled}
      >
        {t('summary:cta', { context: getCTAOptions(), amount: formatMoney(amountDueToday?.amount) })}
      </Button>
    </DockedFooterPortal>
  )
}
